import { PERMISSION_METADATA } from '@/api/permissions';
import type { RoleRequestRecord } from '@/api/role-requests/types';

import { RoleRequestType } from './types';

export type RoleRequestDescription = {
  action: string;
  detail: string;
  context: string | null;
  /** Every permission the change would add, by label — empty when the type names none. */
  permissions: string[];
};

const permissionLabel = (permission: string): string =>
  PERMISSION_METADATA[permission as keyof typeof PERMISSION_METADATA]?.label ?? permission;

const permissionLabels = (permissions: readonly string[]): string[] =>
  permissions.map(permissionLabel).sort((left, right) => left.localeCompare(right));

export const describeRoleRequest = ({
  type,
  payload,
}: RoleRequestRecord): RoleRequestDescription => {
  switch (type) {
    case RoleRequestType.AssignRole:
      return { action: 'Assign role', detail: payload.roleName, context: null, permissions: [] };
    case RoleRequestType.RemoveRole:
      return { action: 'Remove role', detail: payload.roleName, context: null, permissions: [] };
    case RoleRequestType.CreateRole:
      return {
        action: 'Create role',
        detail: payload.name,
        context: payload.sourceRoleName
          ? `Copied from ${payload.sourceRoleName}`
          : 'Created from scratch',
        permissions: permissionLabels(payload.resultingPermissions),
      };
    case RoleRequestType.AddRolePermission:
      return {
        action: 'Add permission',
        detail: permissionLabel(payload.permission),
        context: `To role ${payload.roleName}`,
        permissions: [],
      };
    case RoleRequestType.RemoveRolePermission:
      return {
        action: 'Remove permission',
        detail: permissionLabel(payload.permission),
        context: `From role ${payload.roleName}`,
        permissions: [],
      };
    case RoleRequestType.DeleteRole:
      return { action: 'Delete role', detail: payload.roleName, context: null, permissions: [] };
    case RoleRequestType.RenameRole:
      return {
        action: 'Rename role',
        detail: `${payload.oldName} → ${payload.newName}`,
        context: null,
        permissions: [],
      };
    default: {
      const unknownType: never = type;
      return {
        action: 'Role change',
        detail: String(unknownType),
        context: null,
        permissions: [],
      };
    }
  }
};

export const formatRoleRequestSummary = (request: RoleRequestRecord): string => {
  const { action, detail } = describeRoleRequest(request);
  return `${action} · ${detail}`;
};
